import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchAgentsStatus, fetchAgentActions } from '@/lib/api';
import { useNexusStore } from '@/stores/nexusStore';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, Cpu, Shield, Search, Megaphone, BookOpen, GitBranch, Server, Zap, BrainCircuit, ChevronDown, Scale } from 'lucide-react';

const AGENT_ICONS = {
  'MOA': BrainCircuit, 'ARIA': Zap, 'CHANGE-IQ': GitBranch, 'ATLAS': Server,
  'SENTINL': Shield, 'NOVA': Search, 'CONCIERGE': Bot, 'CAPACITOR': Cpu,
  'HERALD': Megaphone, 'LEARN-X': BookOpen,
};

const AGENT_COLORS = {
  'MOA': '#3B82F6', 'ARIA': '#EF4444', 'CHANGE-IQ': '#F59E0B', 'ATLAS': '#06B6D4',
  'SENTINL': '#EF4444', 'NOVA': '#8B5CF6', 'CONCIERGE': '#10B981', 'CAPACITOR': '#F97316',
  'HERALD': '#EC4899', 'LEARN-X': '#6366F1',
};

export default function AgentsPage() {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const agentStatuses = useNexusStore((s) => s.agentStatuses);
  const activityFeed = useNexusStore((s) => s.activityFeed);

  const { data: agents } = useQuery({
    queryKey: ['agents-status'],
    queryFn: fetchAgentsStatus,
    refetchInterval: 10000,
  });

  const { data: agentActions } = useQuery({
    queryKey: ['agent-actions', selectedAgent],
    queryFn: () => fetchAgentActions(selectedAgent, 30),
    enabled: !!selectedAgent,
  });

  const filteredActivity = selectedAgent
    ? activityFeed.filter(e => e.agent_name === selectedAgent)
    : activityFeed;

  const combinedActions = selectedAgent
    ? [...filteredActivity, ...(agentActions || [])].slice(0, 40)
    : filteredActivity.slice(0, 40);

  return (
    <div data-testid="agents-page" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Agent Studio</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Monitor and manage all AI agents</p>
        </div>
        <Link to="/agents/scaling" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0d3d4d] border border-cyan-500/30 text-[var(--nexus-accent)] text-xs font-semibold hover:bg-[#0f4a5c] transition-colors" data-testid="scaling-link">
          <Scale size={12} /> Scaling & Orchestration
        </Link>
      </div>

      {/* Agent Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4" data-testid="agents-grid">
        {(agents || []).map((agent) => {
          const socketData = agentStatuses[agent.name] || {};
          const merged = { ...agent, ...socketData };
          const Icon = AGENT_ICONS[agent.name] || Bot;
          const color = AGENT_COLORS[agent.name] || '#06B6D4';
          const isActive = merged.status === 'ACTIVE' || merged.status === 'PROCESSING';
          const isSelected = selectedAgent === agent.name;

          return (
            <button
              key={agent.name}
              onClick={() => setSelectedAgent(isSelected ? null : agent.name)}
              data-testid={`agent-studio-card-${agent.name.toLowerCase()}`}
              className={`relative p-3 rounded-xl bg-[var(--nexus-bg-elevated)] border text-left transition-all duration-200 ${
                isSelected ? 'border-[var(--nexus-accent)] shadow-[0_0_0_1px_rgba(6,182,212,0.35)]' : 'border-[var(--nexus-border)]'
              } ${isActive ? 'animate-nexus-breathe' : ''} hover:bg-white/[0.02]`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg" style={{ backgroundColor: `${color}15`, color }}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs font-bold text-[var(--nexus-text-primary)]">{agent.name}</span>
                    <StatusDot status={isActive ? 'active' : 'idle'} pulse={isActive} size="sm" />
                  </div>
                  <p className="text-[10px] text-[var(--nexus-text-muted)] truncate">{agent.description}</p>
                </div>
              </div>

              {merged.current_task && (
                <div className="mb-2 px-2 py-1 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <p className="text-[10px] font-mono text-[var(--nexus-accent)] truncate">{merged.current_task}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-1 text-center">
                <div>
                  <p className="font-mono text-sm font-bold text-[var(--nexus-text-primary)] tabular-nums">{merged.tickets_processed_today || 0}</p>
                  <p className="text-[9px] text-[var(--nexus-text-muted)]">Processed</p>
                </div>
                <div>
                  <p className="font-mono text-sm font-bold text-[var(--nexus-text-primary)] tabular-nums">{merged.confidence?.toFixed(0) || 0}%</p>
                  <p className="text-[9px] text-[var(--nexus-text-muted)]">Confidence</p>
                </div>
                <div>
                  <p className="font-mono text-sm font-semibold text-[var(--nexus-success)] tabular-nums">{merged.success_rate?.toFixed(0) || 0}%</p>
                  <p className="text-[9px] text-[var(--nexus-text-muted)]">Success</p>
                </div>
              </div>

              <div className="mt-2 w-full h-1 rounded-full bg-[var(--nexus-bg-base)] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${merged.confidence || 0}%`, backgroundColor: color }} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Activity Stream */}
      <div className="nexus-card relative overflow-hidden">
        <div className="p-3 border-b border-[var(--nexus-border)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-[var(--nexus-accent)]" />
              <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">
                {selectedAgent ? `${selectedAgent} Activity` : 'All Agent Activity'}
              </h2>
            </div>
            {selectedAgent && (
              <button onClick={() => setSelectedAgent(null)} className="text-[10px] text-[var(--nexus-accent)] hover:underline">Clear filter</button>
            )}
          </div>
        </div>
        <ScrollArea className="h-[350px]">
          <div className="p-2 space-y-1" data-testid="agents-activity-stream">
            {combinedActions.map((entry, idx) => (
              <div key={entry.id || idx} className={`flex items-start gap-2 p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] ${idx === 0 ? 'animate-nexus-slide-in' : ''}`}>
                <StatusDot status={entry.severity || 'info'} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[10px] font-semibold" style={{ color: AGENT_COLORS[entry.agent_name] || '#06B6D4' }}>{entry.agent_name}</span>
                    <span className="font-mono text-[10px] text-[var(--nexus-text-muted)]">
                      {new Date(entry.timestamp).toLocaleTimeString('en-US', { hour12: false })}
                    </span>
                    {entry.ticket_id && (
                      <span className="font-mono text-[10px] px-1 py-0 rounded bg-[var(--nexus-bg-elevated)] text-[var(--nexus-primary-bright)] border border-[var(--nexus-border)]">
                        {entry.ticket_id}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[var(--nexus-text-secondary)]">{entry.action}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-mono text-[10px] text-[var(--nexus-text-muted)] tabular-nums">{entry.confidence?.toFixed(0)}%</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${entry.success !== false ? 'bg-[var(--nexus-success)]' : 'bg-[var(--nexus-danger)]'}`} />
                </div>
              </div>
            ))}
            {combinedActions.length === 0 && (
              <div className="text-center py-8 text-[var(--nexus-text-muted)] text-xs">No activity to display</div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
